import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined"
import { Button, Col, Flex, Form, Input, Row, theme } from "antd"
import Spinner from "components/common/Spinner"
import useGeocoder from "modules/google/hooks/useGeocoder"
import useValidateAddress from "modules/google/hooks/useValidateAddress"
import PropTypes from "prop-types"
import { useState } from "react"
import { CountrySelector } from "ui/Input/CountrySelector"
import { Confirmed } from "ui/Icon/DSIcon/Confirmed"
const { useToken } = theme

export const LocationInfo = ({ form, t, setManualAddress, setCenterLatLng, setMeetingLatLng }) => {
  const geocoder = useGeocoder(null)
  const { token } = useToken()

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [buttonLabel, setButtonLabel] = useState(t("listings.tabs.location.confirm_address")) // Initial label

  const { handleConfirmAddress, loading } = useValidateAddress(
    geocoder,
    form,
    setCenterLatLng,
    setMeetingLatLng,
    setIsButtonDisabled,
    setButtonLabel
  )
  // Handle form value changes
  const handleValuesChange = () => {
    setIsButtonDisabled(false) // Enable the button when any input changes
    setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset label to "Confirm Address"
  }

  // Handle back to search changes
  const handleBack = () => {
    setCenterLatLng(null)
    setMeetingLatLng(null)
    setManualAddress(false)
    form.resetFields() // Reset the form fields
    setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset label to "Confirm Address"
  }

  // Handle confirm address click
  const handleConfirmClick = async () => {
    try {
      await form.validateFields() // This will throw if any fields are invalid

      setButtonLabel(t("listings.tabs.location.confirming")) // Set label to "Confirming..."
      await handleConfirmAddress()
      setButtonLabel(t("listings.tabs.location.confirmed")) // Set label to "Confirmed" after success
    } catch (err) {
      // Validation failed
      setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset to default
    }
  }

  const renderButtonLabel = () => {
    if (buttonLabel === "Confirmed") {
      return (
        <Flex align="center" justify="center">
          <Confirmed className="mr-s" />
          {buttonLabel}
        </Flex>
      )
    }
    return buttonLabel
  }

  return (
    <>
      <button
        className="d-flex align-items-center mb-l"
        onClick={handleBack}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          display: "flex",
          alignItems: "center",
          cursor: "pointer"
        }}
      >
        <ArrowLeftOutlined style={{ color: token.colorPrimary, marginRight: "8px" }} />
        <span className="mb-0" style={{ color: token.colorPrimary }}>
          {t("listings.tabs.location.back_to_search")}
        </span>
      </button>

      <Form form={form} layout="vertical" onValuesChange={handleValuesChange} requiredMark={false}>
        <Form.Item
          name="street_address"
          label={
            <span>
              {t("listings.tabs.location.address.street_address")} <span>*</span>
            </span>
          }
          rules={[{ required: true, message: t("listings.tabs.location.validation.street_address") }]}
        >
          <Input
            placeholder={t("listings.tabs.location.address.street_address")}
            onBlur={(e) => form.setFieldValue("street_address", e.target.value.trim())}
          />
        </Form.Item>

        <Row className="mb-l" gutter={16}>
          <Col span={8}>
            <Form.Item
              name="city"
              label={
                <span>
                  {t("listings.tabs.location.address.city")} <span>*</span>
                </span>
              }
              rules={[{ required: true, message: t("listings.tabs.location.validation.city") }]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.city")}
                onBlur={(e) => form.setFieldValue("city", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="state"
              label={
                <span>
                  {t("listings.tabs.location.address.state")} <span>*</span>
                </span>
              }
              rules={[{ required: true, message: t("listings.tabs.location.validation.state") }]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.state")}
                onBlur={(e) => form.setFieldValue("state", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="zip_code"
              label={
                <span>
                  {t("listings.tabs.location.address.zip_code")} <span>*</span>
                </span>
              }
              rules={[{ required: true, message: t("listings.tabs.location.validation.zip_code") }]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.zip_code")}
                onBlur={(e) => form.setFieldValue("zip_code", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={
            <span>
              {t("listings.tabs.location.address.country")} <span>*</span>
            </span>
          }
          name="country"
          required={true}
          rules={[{ required: true, message: t("listings.tabs.location.validation.country") }]}
        >
          <CountrySelector />
        </Form.Item>
        <Button type="primary" size="large" className="px-6" disabled={isButtonDisabled || loading} onClick={handleConfirmClick}>
          {loading ? (
            <Flex>
              <Spinner className="mr-s" color="white" size="small" />
              {t("listings.tabs.location.confirming")}
            </Flex>
          ) : (
            renderButtonLabel()
          )}
        </Button>
      </Form>
    </>
  )
}

LocationInfo.propTypes = {
  form: PropTypes.object,
  t: PropTypes.func,
  setManualAddress: PropTypes.func,
  setCenterLatLng: PropTypes.func,
  setMeetingLatLng: PropTypes.func
}
